import { mediaPaths, type EventMediaKind } from "@/lib/mediaPaths";
import { storageService } from "./storageService";
import { maybeRouteThroughCdn } from "@/lib/cdnUrl";
import { fromStorageRecord, type CanonicalStorageMetadata, type CanonicalStorageMetadataDB } from "./storage/migrationConfig";

interface UploadEventMediaParams {
  eventSlug: string;
  deviceCode: string;
  mediaId: string;
  file: File | Blob;
  kind: EventMediaKind;
  contentType?: string;
  cacheControl?: string;
  customPath?: string;
}

export const mediaService = {
  buildEventMediaPath(params: {
    eventSlug: string;
    deviceCode: string;
    mediaId: string;
    file: File;
    kind: EventMediaKind;
  }): string {
    return mediaPaths.buildEventMediaPath(params);
  },

  buildThumbnailPath(params: { eventSlug: string; mediaId: string }): string {
    return mediaPaths.buildThumbnailPath(params);
  },

  async uploadEventMedia(params: UploadEventMediaParams): Promise<{
    filePath: string;
    fileUrl: string;
    storageMetadata: CanonicalStorageMetadataDB;
  }> {
    const hasFileCtor = typeof File !== "undefined";
    const isFile = hasFileCtor && params.file instanceof File;
    const filePath = params.customPath || (
      isFile
        ? mediaPaths.buildEventMediaPath({
          eventSlug: params.eventSlug,
          deviceCode: params.deviceCode,
          mediaId: params.mediaId,
          file: params.file as File,
          kind: params.kind,
        })
        : (() => { throw new Error("customPath is required when uploading a Blob."); })()
    );

    const { publicUrl, metadata } = await storageService.uploadWithMetadata(filePath, params.file, {
      contentType: params.contentType,
      cacheControl: params.cacheControl ?? "3600",
    });

    return {
      filePath,
      fileUrl: publicUrl,
      storageMetadata: {
        storage_provider: metadata.provider,
        storage_key: metadata.key,
        storage_url_mode: metadata.urlMode,
      },
    };
  },

  async deleteEventMedia(filePaths: string[]): Promise<void> {
    if (!filePaths.length) return;
    await storageService.remove(filePaths);
  },

  getDisplayUrl(filePath: string): string {
    return storageService.getPublicUrl(filePath);
  },

  async resolveDisplayUrl(row: {
    file_url?: string | null;
    file_path?: string | null;
    storage_provider?: string | null;
    storage_key?: string | null;
    storage_url_mode?: string | null;
  }): Promise<string> {
    if (!row.file_path && !row.storage_key) {
      return maybeRouteThroughCdn(row.file_url || "");
    }

    const metadata: CanonicalStorageMetadata = fromStorageRecord(row);
    return maybeRouteThroughCdn(await storageService.resolvePublicUrl(metadata));
  },

  async getDownloadUrl(filePath: string, expiresInSeconds?: number): Promise<string> {
    try {
      return await storageService.getSignedUrl(filePath, expiresInSeconds);
    } catch {
      return this.getDisplayUrl(filePath);
    }
  },
};
